import { motion } from "framer-motion";
import { CakeSlice } from "lucide-react";
import SubPageLayout, { SubPageCta, SubPageHero, SubPageLoading } from "@/components/public/SubPageLayout";
import { useSiteContent } from "@/lib/content";

const headCls = "text-xs font-semibold uppercase tracking-[0.18em] text-wine/70";

function ServingsTable({ sizes }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="mt-12 overflow-hidden rounded-[2rem] border border-gold/50 bg-white/85 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.35)] backdrop-blur"
      data-testid="guide-table-card"
    >
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 border-b border-gold/30 bg-rose/60 px-6 py-4 sm:px-10">
        <span className={headCls}>Cake size</span>
        <span className={headCls}>From</span>
        <span className={`text-right ${headCls}`}>Serves</span>
      </div>
      {sizes.map((row, i) => (
        <motion.div
          key={row.size}
          initial={{ opacity: 0, x: -18 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.06, duration: 0.5 }}
          className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 border-b border-gold/15 px-6 py-4 transition-colors last:border-0 hover:bg-blush/70 sm:px-10"
          data-testid={`serving-row-${row.size.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
        >
          <span className="flex items-center gap-3 font-serif text-xl sm:text-2xl font-semibold text-burgundy">
            <CakeSlice className="h-5 w-5 shrink-0 text-gold" />
            {row.size}
          </span>
          <span className="font-serif text-xl sm:text-2xl font-semibold text-gold">{row.price}</span>
          <span className="text-right text-sm sm:text-base text-plum/70">{row.serves}</span>
        </motion.div>
      ))}
    </motion.div>
  );
}

export default function ServingsGuide() {
  const content = useSiteContent();
  if (!content) return <SubPageLoading testId="guide-loading" />;

  return (
    <SubPageLayout prefix="guide" pageTestId="servings-guide-page" maxWidth="max-w-3xl">
      <SubPageHero
        prefix="guide"
        eyebrow="Good to know before you order"
        heading={<>Cake Sizes &amp; Servings Guide</>}
        disclaimer={
          <>
            <strong className="text-burgundy">Please note: all prices are a guide only.</strong> Every cake is made to order, so your final price
            will likely change based on your design ideas — size, flavours, toppers, flowers, detailing and finishes. Send through what you’re
            dreaming of and I’ll put together a custom quote for you.
          </>
        }
        disclaimerNote="Serving sizes are suggested portions and may vary depending on cutting style."
      />
      <ServingsTable sizes={content.servings} />
      <SubPageCta prefix="guide" script="Ready when you are" label="Request a Quote" />
    </SubPageLayout>
  );
}
